import posthog, { isPostHogEnabled } from "@/posthog";
import { cacheManager } from "@/services/pwa/cache-manager/cache-manager";
import { simpleUpdateManager } from "@/services/pwa/simple-update-manager/simple-update-manager";
import { captureException } from "@/utils/sentry";
import { Component, type ErrorInfo, type ReactNode } from "react";

import { ErrorDisplay } from "./components/error-display/ErrorDisplay";
import { RecoveryActions } from "./components/recovery-actions/RecoveryActions";

import styles from "./ErrorBoundary.module.css";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  isClearing: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      isClearing: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isChunkError =
      error.name === "ChunkLoadError" ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("Failed to fetch dynamically imported module");

    if (isPostHogEnabled) {
      posthog.captureException(error, {
        component_stack: errorInfo.componentStack,
        error_boundary: true,
        is_chunk_error: isChunkError,
      });
    }

    if (isChunkError) {
      const reloadKey = "chunk-error-reload";
      if (!sessionStorage.getItem(reloadKey)) {
        captureException(error, {
          type: "chunk-load-error",
          autoReloading: true,
        });
        sessionStorage.setItem(reloadKey, "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem(reloadKey);
    }

    captureException(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      isChunkError,
    });

    console.error("❌ [ErrorBoundary] Uncaught error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleClearSession = async (): Promise<void> => {
    this.setState({ isClearing: "session" });
    try {
      await cacheManager.clearCurrentSession();
      window.location.href = "/";
    } catch (error) {
      console.error("Failed to clear session:", error);
      this.setState({ isClearing: null });
    }
  };

  handleClearConfig = async (): Promise<void> => {
    this.setState({ isClearing: "config" });
    try {
      await cacheManager.clearClientConfig();
      window.location.href = "/";
    } catch (error) {
      console.error("Failed to clear config:", error);
      this.setState({ isClearing: null });
    }
  };

  handleUpdate = async (): Promise<void> => {
    this.setState({ isClearing: "update" });
    try {
      await simpleUpdateManager.applyUpdate();
    } catch (error) {
      console.error("Failed to update:", error);
      this.setState({ isClearing: null });
    }
  };

  handleFreshInstall = async (): Promise<void> => {
    this.setState({ isClearing: "fresh" });
    try {
      await cacheManager.nuclearReset();
      window.location.href = "/";
    } catch (error) {
      console.error("Failed to perform fresh install:", error);
      this.setState({ isClearing: null });
    }
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className={styles.errorBoundary}>
          <div className={styles.errorContainer}>
            <ErrorDisplay
              error={this.state.error}
              errorInfo={this.state.errorInfo}
            />
            <RecoveryActions
              isClearing={this.state.isClearing}
              onReload={this.handleReload}
              onClearSession={this.handleClearSession}
              onClearConfig={this.handleClearConfig}
              onUpdate={this.handleUpdate}
              onFreshInstall={this.handleFreshInstall}
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
